/**
 * The HTTP contract between the operator tablet and the control API (`/api/v1`).
 *
 * Request bodies are Zod schemas so the API can validate them at the boundary; the tablet imports
 * the inferred types only (`import type`), so Zod never reaches the client bundle.
 */
import { z } from 'zod';

export const API_PREFIX = '/api/v1';

// ── shared primitives ───────────────────────────────────────────────────────

/** A robot (unit) is known by its name, as on its label (e.g. EVE20049); its database id is a number. */
export const UnitId = z.string().regex(/^[A-Z0-9-]{3,32}$/, 'unit id must be 3–32 of A–Z, 0–9, -');
/** Operators, hospitals and tablets are numbered 1, 2, 3… */
export const NumberId = z.number().int().positive();
export const Pin = z.string().regex(/^\d{4}$/, 'PIN must be 4 digits');
export const WardCode = z.string().regex(/^[A-Z0-9]{1,8}$/);
export const BedNumber = z.string().regex(/^\d{2}$/);
export const AbortReason = z.enum([
  'MANUAL_EMERGENCY_STOP',
  'OCCUPANCY_BREACH',
  'DEVICE_FAULT',
  /** The relay was found off mid-cycle (switched in the Shelly app, at the wall, or power lost). */
  'LAMP_INTERRUPTED',
]);
export type AbortReason = z.infer<typeof AbortReason>;

// ── sessions ────────────────────────────────────────────────────────────────

/** POST /sessions */
export const CreateSessionRequest = z.object({ unitId: UnitId, pin: Pin });
export type CreateSessionRequest = z.infer<typeof CreateSessionRequest>;

export interface SessionResponse {
  /** Bearer token for subsequent requests. */
  readonly token: string;
  readonly operator: {
    readonly id: number;
    readonly name: string;
    /** A service account (the Evesix team): no hospital, signs in on every robot. */
    readonly service: boolean;
    /**
     * Their completed cycles mark rooms sanitised. False only for service accounts not allowed real
     * cycles: their runs are tests, recorded as kind 'service'.
     */
    readonly realCycles: boolean;
  };
  readonly expiresAt: string;
}

// ── catalog ─────────────────────────────────────────────────────────────────

export interface WardDto {
  readonly code: string;
  readonly name: string;
  readonly beds: number;
  /** ISO time of the most recent completed cycle in the ward, or null if none. */
  readonly lastCycleAt: string | null;
}

/** GET /units/:unitId */
export interface UnitCatalogResponse {
  readonly unit: { readonly id: string; readonly hospitalName: string };
  /** Lamp outputs (bulbs) wired to this unit, 0-based — what a cycle can choose from. */
  readonly lampChannels: readonly number[];
  readonly wards: readonly WardDto[];
  /** `WARD-BED` keys sanitised within the current window (e.g. "2A-03"). */
  readonly sanitisedBeds: readonly string[];
  /** The cycle running on this unit right now, so a reloaded tablet can pick it back up. */
  readonly activeCycle: ActiveCycleDto | null;
}

export interface ActiveCycleDto {
  readonly cycleId: string;
  readonly wardCode: string;
  readonly bed: string;
  readonly positionId: string;
  readonly positionLabel: string;
  readonly operatorName: string;
  readonly durationSeconds: number;
  readonly wallClockSeconds: number;
  readonly channels: readonly number[];
  readonly startedAt: string;
}

/** GET /units/:unitId/status — readable without a session so the header works before login. */
export interface UnitStatusResponse {
  readonly unitId: string;
  /** Whether the lamp controller is reachable. */
  readonly connected: boolean;
  /** Relay state as last reported by the controller; null when unknown. */
  readonly lampOn: boolean | null;
  /** Power drawn through the lamp relay in watts, when the controller meters it. */
  readonly lampPowerW: number | null;
  /** Always null: no robot reports a battery. Kept because tablet apps up to 0.6 display it. */
  readonly batteryPct: null;
  readonly activeCycleId: string | null;
  /** Set while the lamp is on from a manual command: when its auto-off fires. */
  readonly manualAutoOffAt: string | null;
  /** The unit's most recent cycle to end (last 10 minutes), so a tablet can say why it stopped. */
  readonly lastEndedCycle: EndedCycleDto | null;
}

export interface EndedCycleDto {
  readonly cycleId: string;
  /**
   * completed = the full planned time; cut = ended early (see abortReason); not_confirmed = no
   * tablet ever confirmed it, so the room is not on record as sanitised.
   */
  readonly status: 'completed' | 'cut' | 'not_confirmed';
  /** Why a cut cycle ended; null otherwise. */
  readonly abortReason: AbortReason | null;
}

// ── cycles ──────────────────────────────────────────────────────────────────

/** POST /cycles */
export const StartCycleRequest = z.object({
  wardCode: WardCode,
  bed: BedNumber,
  positionId: z.string().regex(/^[A-Z0-9_-]{1,32}$/),
  positionLabel: z.string().min(1).max(64),
  /** Protocol exposure time. */
  durationSeconds: z.number().int().min(60).max(3600),
  /** Real seconds the lamp stays on; less than `durationSeconds` only in time-compressed demos. */
  wallClockSeconds: z.number().min(5).max(3600),
  /** Lamp outputs (bulbs) to power, 0-based. Omitted: every output wired to the unit. */
  channels: z.array(z.number().int().min(0).max(15)).min(1).max(16).optional(),
});
export type StartCycleRequest = z.infer<typeof StartCycleRequest>;

export interface CycleStartedResponse {
  readonly cycleId: string;
  readonly startedAt: string;
  /** When the controller's own failsafe switches the lamp off if nothing else does. */
  readonly endsAt: string;
}

/** POST /cycles/:cycleId/complete → the audit record. */
export interface CycleRecordDto {
  readonly cycleId: string;
  readonly logId: string;
  readonly wardCode: string;
  readonly wardName: string;
  readonly bed: string;
  readonly positionId: string;
  readonly positionLabel: string;
  readonly operatorName: string;
  readonly durationSeconds: number;
  readonly wallClockSeconds: number;
  /** Lamp outputs (bulbs) that were powered, 0-based. */
  readonly channels: readonly number[];
  readonly startedAt: string;
  readonly completedAt: string;
}

/** POST /units/:unitId/emergency-stop — accepted without a session: stopping must never be blocked. */
export const EmergencyStopRequest = z.object({
  reason: AbortReason,
  cycleId: z.string().uuid().optional(),
});
export type EmergencyStopRequest = z.infer<typeof EmergencyStopRequest>;

export interface EmergencyStopResponse {
  /** True once the controller acknowledged the off command. */
  readonly lampOff: boolean;
  readonly abortedCycleId: string | null;
}

// ── manual lamp control ─────────────────────────────────────────────────────

/**
 * POST /units/:unitId/lamp — switch the relay by hand, outside a disinfection cycle.
 * Switching on needs a session and always carries an auto-off; switching off never needs one.
 */
export const LampCommandRequest = z.discriminatedUnion('on', [
  z.object({ on: z.literal(true), seconds: z.number().int().min(5).max(3600) }),
  z.object({ on: z.literal(false) }),
]);
export type LampCommandRequest = z.infer<typeof LampCommandRequest>;

export interface LampCommandResponse {
  /** True once the controller acknowledged the command. */
  readonly acknowledged: boolean;
  readonly lampOn: boolean;
  /** When the controller's own timer switches a manually-lit lamp off. */
  readonly autoOffAt: string | null;
  /** Switching off during a cycle aborts it. */
  readonly abortedCycleId: string | null;
}

// ── tablets: direct control and offline sync ───────────────────────────────
//
// A tablet that drives its robot's switch directly over Wi-Fi works with no data: it signs operators
// in against PIN checks it downloaded, runs cycles, and keeps every record until it can upload.

/** POST /tablets — enrols this tablet for the signed-in robot. Needs a service-account session. */
export const EnrollTabletRequest = z.object({ appVersion: z.string().max(32) });
export type EnrollTabletRequest = z.infer<typeof EnrollTabletRequest>;

export interface EnrollTabletResponse {
  readonly tabletId: number;
  /** Sent as `Authorization: Tablet <token>`; store it on the device only. */
  readonly token: string;
}

/**
 * POST /setup — the first step of setting up a tablet: a service account's code, with no robot
 * yet. Answers with every active hospital and its robots that have no tablet, to choose from.
 */
export const SetupRequest = z.object({ pin: Pin });
export type SetupRequest = z.infer<typeof SetupRequest>;

export interface SetupRobotDto {
  /** The robot's name, as on its label: what the API knows it by. */
  readonly id: string;
  readonly serial: string | null;
  /** Names of the wards it is kept to; empty when it covers the whole hospital. */
  readonly wards: readonly string[];
}

export interface SetupHospitalDto {
  /** The hospital's number, as text: tablet apps up to 0.6 match it against a form value. */
  readonly id: string;
  readonly name: string;
  /** In service, with no tablet: a robot has one tablet, so taken robots are never offered. */
  readonly robots: readonly SetupRobotDto[];
}

export interface SetupResponse {
  /** Short-lived; sent back with the chosen robot. */
  readonly token: string;
  readonly operatorName: string;
  readonly expiresAt: string;
  readonly hospitals: readonly SetupHospitalDto[];
}

/** POST /tablets/setup — sets the tablet up for the robot chosen. */
export const SetupTabletRequest = z.object({
  token: z.string().min(20).max(400),
  robotId: UnitId,
  appVersion: z.string().max(32),
});
export type SetupTabletRequest = z.infer<typeof SetupTabletRequest>;

export { OFFLINE_PIN_CHECK, SERVICE_SCOPE } from './offline';

export interface OfflineOperatorDto {
  readonly id: number;
  readonly name: string;
  readonly service: boolean;
  /** Service accounts' runs never mark a bed as sanitised unless this is set. */
  readonly mayRunRealCycles: boolean;
  /** See OFFLINE_PIN_CHECK; salted with the operator's own scope. */
  readonly pinCheck: string;
}

/** GET /tablets/current/pack — everything the tablet needs to work with no data. */
export interface OfflinePackResponse {
  readonly generatedAt: string;
  readonly robot: {
    /** The robot's name, as on its label. */
    readonly id: string;
    /** Also the scope of its hospital operators' PIN checks, as text. */
    readonly hospitalId: number;
    readonly hospitalName: string;
    readonly outputs: readonly number[];
    /** Shelly device id (12 hex characters), to recognise the switch on the network. */
    readonly shellyDeviceId: string | null;
    /** Password of the switch's local web interface (user "admin"). */
    readonly switchPassword: string | null;
    /** Watts each lamp output must draw; 0 = not checked. */
    readonly minPowerW: number;
  };
  readonly wards: readonly WardDto[];
  readonly sanitisedBeds: readonly string[];
  readonly sanitisedWindowSeconds: number;
  /** Active operators of the robot's hospital, and active service accounts. */
  readonly operators: readonly OfflineOperatorDto[];
  readonly lockout: { readonly maxFailures: number; readonly windowSeconds: number };
  readonly sessionTtlSeconds: number;
  readonly manualLampMaxSeconds: number;
}

/** A bed cleaned by a completed cycle, and when: the tablet ages it out of its window itself. */
export interface SanitisedBedDto {
  /** `<ward code>-<bed>`, e.g. "2A-03". */
  readonly bed: string;
  readonly at: string;
}

/** The settings a tablet works with offline: they change only with a deploy. */
export interface OfflineSettingsDto {
  readonly sanitisedWindowSeconds: number;
  readonly lockout: OfflinePackResponse['lockout'];
  readonly sessionTtlSeconds: number;
  readonly manualLampMaxSeconds: number;
}

/**
 * GET /tablets/current/changes?since=<cursor> — only what changed for this tablet since its last
 * sync, so an idle sync downloads almost nothing. Without `since` (first sync, or the tablet's
 * daily refresh), or when the robot itself changed, the answer is `full`: everything, replacing
 * what the tablet has.
 */
export interface PackChangesResponse {
  /** Send back as `since` next time. */
  readonly cursor: string;
  readonly full: boolean;
  /** Present when full. */
  readonly robot?: OfflinePackResponse['robot'];
  readonly settings?: OfflineSettingsDto;
  /** New or changed wards (their name, bed count or last cycle). */
  readonly wards: readonly WardDto[];
  /** Codes of wards closed since. */
  readonly removedWards: readonly string[];
  /** New or changed operators who may sign in. */
  readonly operators: readonly OfflineOperatorDto[];
  /** Ids of operators who may no longer sign in (suspended, deactivated, moved). */
  readonly removedOperators: readonly number[];
  /** Beds cleaned since (when full: within the sanitised window). */
  readonly sanitised: readonly SanitisedBedDto[];
}

const IsoTime = z.iso.datetime();
const ClientId = z.string().uuid();

export const SyncSession = z.object({
  id: ClientId,
  operatorId: NumberId,
  startedAt: IsoTime,
  endedAt: IsoTime.nullable(),
  /** Signed in on the tablet with no data (checked against the offline PIN check). */
  offline: z.boolean(),
});
export type SyncSession = z.infer<typeof SyncSession>;

export const SyncCycle = z.object({
  id: ClientId,
  operatorId: NumberId,
  wardCode: WardCode,
  bed: BedNumber,
  positionId: z.string().regex(/^[A-Z0-9_-]{1,32}$/),
  positionLabel: z.string().min(1).max(64),
  durationSeconds: z.number().int().min(60).max(3600),
  wallClockSeconds: z.number().min(5).max(3600),
  channels: z.array(z.number().int().min(0).max(15)).min(1).max(16),
  /**
   * completed only when the switch measured every chosen output on for the full time; cut = it
   * ended early; not_confirmed = the switch's own record could not be read.
   */
  status: z.enum(['completed', 'cut', 'not_confirmed']),
  reason: z.string().max(64).nullable(),
  reasonDetail: z.string().max(200).nullable(),
  startedAt: IsoTime,
  finishedAt: IsoTime,
  /** Tablet uptime at the start: cannot be changed by hand, so it checks the tablet's clock. */
  tabletUptimeMs: z.number().int().min(0).nullable(),
  /** The switch's record of this run, and each chosen output's measured on-time (same order). */
  switchRecordSeq: z.number().int().min(1).nullable(),
  onMs: z.array(z.number().int().min(0)).max(16),
});
export type SyncCycle = z.infer<typeof SyncCycle>;

export const SyncEvent = z.object({
  id: ClientId,
  at: IsoTime,
  type: z.string().regex(/^[a-z_]{1,64}$/),
  operatorId: NumberId.nullable(),
  cycleId: z.string().max(64).nullable(),
  detail: z.record(z.string(), z.unknown()).nullable(),
});
export type SyncEvent = z.infer<typeof SyncEvent>;

/** POST /tablets/current/sync — uploads records kept on the tablet. Safe to repeat. */
export const SyncRequest = z.object({
  clientTime: IsoTime,
  sessions: z.array(SyncSession).max(200),
  cycles: z.array(SyncCycle).max(100),
  events: z.array(SyncEvent).max(500),
});
export type SyncRequest = z.infer<typeof SyncRequest>;

export interface SyncResponse {
  /** Ids now on the server (including ones it already had): safe to delete on the tablet. */
  readonly accepted: readonly string[];
  /** Records the server will never take; kept on the tablet for a person to look at. */
  readonly rejected: readonly { readonly id: string; readonly reason: string }[];
  readonly serverTime: string;
}

// ── errors ──────────────────────────────────────────────────────────────────

export type ApiErrorCode =
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'direct_control'
  /** The robot already has a tablet: reset that one first. */
  | 'robot_taken'
  | 'invalid_pin'
  | 'locked'
  | 'not_found'
  | 'cycle_in_progress'
  | 'cycle_not_running'
  | 'cycle_too_short'
  | 'time_compression_disabled'
  | 'manual_limit_exceeded'
  | 'lamp_unavailable'
  | 'lamp_not_configured'
  | 'internal';

export interface ApiErrorBody {
  readonly error: ApiErrorCode;
  readonly message: string;
  readonly retryAfterSeconds?: number;
}
